import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, User, Mail, Phone, Globe, DollarSign, ShoppingBag, TrendingUp, Calendar, Eye, Search, ShoppingCart, Heart, MousePointer, Clock, CreditCard, Tag, Star } from 'lucide-react';
import { customersApi } from '../../api/endpoints/customers';
import { StatCard } from '../../components/ui/StatCard';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { CurrencyDisplay } from '../../components/ui/CurrencyDisplay';
import { formatDate } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';
import type { OrderStatus } from '../../types/order';

interface RecentOrder {
  _id: string;
  orderNumber?: number | string;
  total: number;
  status: OrderStatus;
  createdAt: string;
  currency?: string;
}

export function CustomerDetails() {
  const { storeId, customerId } = useParams<{ storeId: string; customerId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentStore } = useStore();

  const [timelineBefore, setTimelineBefore] = useState<string | undefined>();

  const { data, isLoading } = useQuery({
    queryKey: ['customer', storeId, customerId],
    queryFn: () => customersApi.get(storeId!, customerId!),
    enabled: !!storeId && !!customerId,
  });

  const { data: timelineData, isLoading: timelineLoading } = useQuery({
    queryKey: ['customer-timeline', storeId, customerId, timelineBefore],
    queryFn: () => customersApi.getTimeline(storeId!, customerId!, { limit: 30, before: timelineBefore }),
    enabled: !!storeId && !!customerId,
  });

  const prettifyUrl = (url?: string, title?: string) => {
    if (!url) return title || '';
    const relative = url.replace(/^\/(storepay|storefront)\/[^/]+\/?/, '');
    if (!relative || relative === '/') return 'الرئيسية';
    if (relative.startsWith('product/')) return title || 'صفحة منتج';
    if (relative.startsWith('category/')) return title || 'صفحة تصنيف';
    if (relative === 'cart') return 'السلة';
    if (relative === 'checkout') return 'إتمام الشراء';
    if (relative.startsWith('search')) return 'البحث';
    if (relative === 'wishlist') return 'المفضلة';
    if (relative === 'account' || relative.startsWith('account')) return 'حسابي';
    if (relative === 'orders' || relative.startsWith('orders')) return 'طلباتي';
    return title || url;
  };

  if (isLoading) return <LoadingSpinner fullPage />;
  if (!data) return null;

  const { customer, recentOrders } = data;
  const currency = currentStore?.currency || 'SAR';
  const orders = (recentOrders ?? []) as RecentOrder[];
  const ordersCount = customer.ordersCount ?? 0;
  const totalSpent = customer.totalSpent ?? 0;
  const avgOrderValue = ordersCount > 0 ? totalSpent / ordersCount : 0;

  return (
    <div className="mx-auto max-w-4xl">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate(`/${storeId}/customers`)}
          className="rounded-lg p-2 text-text-muted hover:bg-bg-hover hover:text-text-primary"
        >
          <ArrowRight className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-bold">{t('customerDetails.title')}</h1>
      </div>

      {/* Customer Profile Card */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
            <User className="h-7 w-7" />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <h2 className="text-lg font-semibold">{customer.realName || customer.name || '-'}</h2>
            <div className="grid gap-2 sm:grid-cols-2">
              {customer.email && (
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <Mail className="h-4 w-4 shrink-0" />
                  <span className="truncate">{customer.email}</span>
                </div>
              )}
              {customer.phoneNumber && (
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <Phone className="h-4 w-4 shrink-0" />
                  <span dir="ltr" className="tabular-nums">{customer.phoneCountryCode ? `+${customer.phoneCountryCode} ${customer.phoneNumber}` : String(customer.phoneNumber)}</span>
                </div>
              )}
              {customer.country && (
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <Globe className="h-4 w-4 shrink-0" />
                  <span>{customer.country}</span>
                </div>
              )}
              {customer.createdAt && (
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <Calendar className="h-4 w-4 shrink-0" />
                  <span>{t('customerDetails.memberSince')}: {formatDate(customer.createdAt)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Row */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          title={t('customerDetails.totalSpent')}
          value={<CurrencyDisplay amount={totalSpent} currency={currency} />}
          icon={<DollarSign className="h-5 w-5" />}
        />
        <StatCard
          title={t('customers.orders')}
          value={ordersCount}
          icon={<ShoppingBag className="h-5 w-5" />}
        />
        <StatCard
          title={t('customerDetails.lifetime')}
          value={<CurrencyDisplay amount={avgOrderValue} currency={currency} />}
          icon={<TrendingUp className="h-5 w-5" />}
        />
        <StatCard
          title={t('customerDetails.memberSince')}
          value={customer.createdAt ? formatDate(customer.createdAt) : '-'}
          icon={<Calendar className="h-5 w-5" />}
        />
      </div>

      {/* Recent Orders */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
        <h2 className="text-base font-semibold mb-4">{t('customerDetails.recentOrders')}</h2>
        {orders.length === 0 ? (
          <p className="text-sm text-text-muted">{t('customerDetails.noOrders')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-default text-text-secondary">
                  <th className="pb-3 text-start font-medium">#</th>
                  <th className="pb-3 text-start font-medium">{t('common.date')}</th>
                  <th className="pb-3 text-start font-medium">{t('common.total')}</th>
                  <th className="pb-3 text-start font-medium">{t('common.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-default">
                {orders.map((order) => {
                  const displayNumber = order.orderNumber ?? order._id?.slice?.(-6) ?? '';
                  const orderCurrency = order.currency || currency;
                  return (
                    <tr
                      key={order._id}
                      onClick={() => navigate(`/${storeId}/orders/${order._id}`)}
                      className="cursor-pointer hover:bg-bg-hover transition-colors"
                    >
                      <td className="py-3 font-medium text-accent">#{displayNumber}</td>
                      <td className="py-3 text-text-secondary">{formatDate(order.createdAt)}</td>
                      <td className="py-3">
                        <CurrencyDisplay amount={order.total} currency={orderCurrency} />
                      </td>
                      <td className="py-3">
                        <StatusBadge status={order.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Customer Activity Timeline */}
      <section className="mt-6 rounded-xl border border-border-default bg-bg-secondary p-6">
        <h2 className="text-base font-semibold mb-4">{t('customerDetails.activityTimeline', 'نشاط العميل')}</h2>
        {timelineLoading ? (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        ) : !timelineData?.events?.length ? (
          <p className="text-sm text-text-muted">{t('customerDetails.noActivity', 'لا يوجد نشاط مسجل بعد')}</p>
        ) : (
          <div className="space-y-0">
            {timelineData.events.map((entry, i) => {
              const iconMap: Record<string, React.ReactNode> = {
                page_view: <Eye className="h-3.5 w-3.5" />,
                product_view: <ShoppingBag className="h-3.5 w-3.5" />,
                add_to_cart: <ShoppingCart className="h-3.5 w-3.5" />,
                remove_from_cart: <ShoppingCart className="h-3.5 w-3.5" />,
                search: <Search className="h-3.5 w-3.5" />,
                checkout_start: <CreditCard className="h-3.5 w-3.5" />,
                checkout_complete: <CreditCard className="h-3.5 w-3.5" />,
                order_complete: <CreditCard className="h-3.5 w-3.5" />,
                wishlist_add: <Heart className="h-3.5 w-3.5" />,
                wishlist_remove: <Heart className="h-3.5 w-3.5" />,
                page_leave: <Clock className="h-3.5 w-3.5" />,
                click: <MousePointer className="h-3.5 w-3.5" />,
                coupon_applied: <Tag className="h-3.5 w-3.5" />,
                review_submitted: <Star className="h-3.5 w-3.5" />,
              };

              const colorMap: Record<string, string> = {
                page_view: 'text-blue-500 bg-blue-500/10',
                product_view: 'text-purple-500 bg-purple-500/10',
                add_to_cart: 'text-green-500 bg-green-500/10',
                remove_from_cart: 'text-orange-500 bg-orange-500/10',
                search: 'text-amber-500 bg-amber-500/10',
                checkout_start: 'text-cyan-500 bg-cyan-500/10',
                checkout_complete: 'text-emerald-500 bg-emerald-500/10',
                order_complete: 'text-emerald-500 bg-emerald-500/10',
                wishlist_add: 'text-pink-500 bg-pink-500/10',
                wishlist_remove: 'text-pink-500 bg-pink-500/10',
                page_leave: 'text-gray-400 bg-gray-400/10',
                coupon_applied: 'text-yellow-500 bg-yellow-500/10',
                review_submitted: 'text-amber-500 bg-amber-500/10',
              };

              const d = entry.data as Record<string, unknown>;

              const describeEvent = () => {
                switch (entry.event) {
                  case 'page_view': return `${t('customerTimeline.viewedPage', 'شاهد صفحة')} ${(d.productName as string) || prettifyUrl(d.url as string, d.title as string)}`;
                  case 'product_view': return `${t('customerTimeline.viewedProduct', 'شاهد منتج')} ${(d.productName as string) || (d.title as string) || ''}`;
                  case 'add_to_cart': return `${t('customerTimeline.addedToCart', 'أضاف للسلة')} ${(d.productName as string) || (d.title as string) || ''} ${(d.quantity as number) > 1 ? `×${d.quantity}` : ''}`;
                  case 'remove_from_cart': return `${t('customerTimeline.removedFromCart', 'أزال من السلة')} ${(d.productName as string) || (d.title as string) || ''}`;
                  case 'search': return `${t('customerTimeline.searched', 'بحث عن')} "${(d.query as string) || ''}"${(d.resultCount as number) !== undefined ? ` (${d.resultCount} ${t('customerTimeline.results', 'نتيجة')})` : ''}`;
                  case 'checkout_start': return `${t('customerTimeline.startedCheckout', 'بدأ الشراء')} - ${d.itemCount || 0} ${t('customerTimeline.items', 'منتجات')}`;
                  case 'checkout_complete':
                  case 'order_complete': return `${t('customerTimeline.completedOrder', 'أتم طلب')} #${(d.orderId as string) || ''}`;
                  case 'wishlist_add': return `${t('customerTimeline.addedToWishlist', 'أضاف للمفضلة')} ${(d.productName as string) || (d.title as string) || ''}`;
                  case 'wishlist_remove': return `${t('customerTimeline.removedFromWishlist', 'أزال من المفضلة')} ${(d.productName as string) || (d.title as string) || ''}`;
                  case 'page_leave': return `${t('customerTimeline.leftPage', 'غادر')} ${(d.productName as string) || prettifyUrl(d.url as string, d.title as string)}${(d.duration as number) ? ` - ${d.duration}${t('customerTimeline.seconds', 'ث')}` : ''}`;
                  case 'coupon_applied': return d.success ? `${t('customerTimeline.couponApplied', 'استخدم كوبون')} "${(d.code as string) || ''}"` : `${t('customerTimeline.couponFailed', 'حاول استخدام كوبون')} "${(d.code as string) || ''}" - ${(d.error as string) || t('customerTimeline.invalid', 'غير صالح')}`;
                  case 'review_submitted': return `${t('customerTimeline.reviewSubmitted', 'قيّم منتج')} ${(d.title as string) || ''} - ${'★'.repeat((d.rating as number) || 0)}`;
                  default: return entry.event;
                }
              };

              const isLast = i === timelineData.events.length - 1;

              return (
                <div key={`${entry.timestamp}-${i}`} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${colorMap[entry.event] || 'text-gray-500 bg-gray-500/10'}`}>
                      {iconMap[entry.event] || <Eye className="h-3.5 w-3.5" />}
                    </div>
                    {!isLast && <div className="w-px flex-1 bg-border-default" />}
                  </div>
                  <div className={`pb-4 min-w-0 flex-1 ${isLast ? '' : ''}`}>
                    <p className="text-sm text-text-primary leading-snug">{describeEvent()}</p>
                    <p className="text-xs text-text-muted mt-0.5">{formatDate(entry.timestamp)}</p>
                  </div>
                </div>
              );
            })}
            {timelineData.hasMore && (
              <button
                onClick={() => {
                  const last = timelineData.events[timelineData.events.length - 1];
                  if (last) setTimelineBefore(last.timestamp);
                }}
                className="w-full py-2 text-xs text-accent hover:text-accent/80 font-medium transition-colors"
              >
                {t('common.loadMore', 'عرض المزيد')}
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
